import { Link } from 'react-router-dom';
import { ArrowRight, Envelope, Lock } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AuthBrandPanel } from '../components/layout/AuthBrandPanel';
import { portalRoutes } from '../lib/portalRoutes';
import { getPortalDesignBootstrap } from '../lib/portalDesignBootstrap';

export default function Login() {
  const bootstrap = window.portalReactBootstrap?.login ?? {};
  const portalDesign = getPortalDesignBootstrap();
  const loginError = typeof bootstrap.error === 'string' && bootstrap.error.trim() !== ''
    ? bootstrap.error.replace('Invalid credentials.', 'E-Mail oder Passwort sind nicht korrekt.')
    : '';
  const hasLoginError = loginError !== '';

  const fieldClassName = hasLoginError
    ? 'pl-10 border-red-300 bg-red-50/60 text-red-900 placeholder:text-red-300 focus-visible:border-red-500'
    : 'pl-10';

  return (
    <div className="grid min-h-screen bg-background text-foreground lg:grid-cols-[minmax(520px,680px)_1fr]">
      <div className="flex flex-col justify-center px-6 py-10 sm:px-10 lg:border-r lg:border-border lg:px-16 lg:py-12">
        <Link to={portalRoutes.login} className="mb-9 inline-flex self-start">
          {portalDesign.logoUrl ? (
            <img
              src={portalDesign.logoUrl}
              alt={portalDesign.portalName}
              className="h-10 w-auto max-w-[220px] object-contain"
            />
          ) : (
            <span className="font-heading text-2xl font-extrabold">{portalDesign.portalName}</span>
          )}
        </Link>

        <div className="mb-1 flex items-center gap-3">
          <div className="h-10 w-2.5 shrink-0 -skew-x-12 bg-primary" />
          <h1 className="font-heading text-4xl font-black leading-none">
            Willkommen <span className="text-primary">zurück</span>
          </h1>
        </div>
        <p className="mb-7 mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
          Melde dich im Händler-Portal an, um Aufträge, Files und Credits zu verwalten.
        </p>

        <div className="mb-6 flex max-w-md gap-1.5 rounded-md border border-border bg-secondary p-1.5">
          <span className="flex-1 rounded-sm bg-primary py-2.5 text-center font-heading text-sm font-bold text-primary-foreground">
            Anmelden
          </span>
          <Link
            to={portalRoutes.register}
            className="flex-1 rounded-sm py-2.5 text-center font-heading text-sm font-bold text-muted-foreground no-underline transition-colors hover:text-foreground"
          >
            Registrieren
          </Link>
        </div>

        <form method="post" action={portalRoutes.login} className="max-w-md space-y-5">
          <input type="hidden" name="_csrf_token" value={bootstrap.csrfToken || ''} />

          {hasLoginError ? (
            <div
              className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="login-error"
              role="alert"
              aria-live="polite"
            >
              <div className="font-semibold text-red-800">Anmeldung fehlgeschlagen</div>
              <div className="mt-1">{loginError}</div>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="email" className="font-mono text-[11px] text-muted-foreground">E-Mail</Label>
            <div className="relative">
              <Envelope className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                defaultValue={bootstrap.lastUsername || ''}
                placeholder="kunde@beispiel.de"
                autoComplete="email"
                required
                aria-invalid={hasLoginError}
                className={fieldClassName}
                data-testid="login-email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="font-mono text-[11px] text-muted-foreground">Passwort</Label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                required
                aria-invalid={hasLoginError}
                className={fieldClassName}
                data-testid="login-password"
              />
            </div>
          </div>

          <div className="flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2 text-muted-foreground">
              <input type="checkbox" className="h-4 w-4 rounded-sm border-border accent-[hsl(var(--primary))]" />
              Angemeldet bleiben
            </label>
            <Link to={portalRoutes.passwordResetRequest} className="text-primary hover:underline">
              Passwort vergessen?
            </Link>
          </div>

          <Button className="btn-gradient h-11 w-full text-white" type="submit" data-testid="login-submit">
            Anmelden
            <ArrowRight className="ml-1 h-4 w-4" weight="bold" />
          </Button>
        </form>

        <div className="mt-6 max-w-md text-sm text-muted-foreground">
          Noch kein Händler-Konto?{' '}
          <Link to={portalRoutes.register} className="text-primary hover:underline">
            Jetzt registrieren
          </Link>
        </div>
      </div>

      <AuthBrandPanel portalName={portalDesign.portalName} />
    </div>
  );
}
